import { createTheme, type ThemeOptions, type PaletteMode } from '@mui/material/styles';

import { colors, shape, spacingUnit, typography } from './tokens';

function buildThemeOptions(mode: PaletteMode): ThemeOptions {
  const isDark = mode === 'dark';
  const surface = isDark ? colors.dark : colors.light;

  return {
    palette: {
      mode,
      primary: {
        light:        colors.primary[400],
        main:         colors.primary[500],
        dark:         colors.primary[700],
        contrastText: '#ffffff',
      },
      secondary: {
        light:        colors.secondary[400],
        main:         colors.secondary[500],
        dark:         colors.secondary[700],
        contrastText: '#000000',
      },
      error:   colors.error,
      warning: colors.warning,
      info:    colors.info,
      success: colors.success,
      background: {
        default: surface.bg,
        paper:   surface.surface,
      },
      text: isDark
        ? { primary: '#FFFFFF', secondary: 'rgba(255,255,255,0.60)', disabled: 'rgba(255,255,255,0.35)' }
        : { primary: '#111827', secondary: '#6b7280',                 disabled: '#9ca3af' },
      grey: colors.grey,
      divider: surface.border,
      subtleFill: {
        main: isDark ? 'rgba(255,255,255,0.04)' : colors.grey[50],
        light: isDark ? 'rgba(255,255,255,0.025)' : '#F9FAFB',
        dark: isDark ? 'rgba(255,255,255,0.06)' : colors.grey[100],
      },
    },

    typography: {
      fontFamily:       typography.fontFamilyBase,
      fontWeightLight:  typography.fontWeightLight,
      fontWeightRegular:typography.fontWeightRegular,
      fontWeightMedium: typography.fontWeightMedium,
      fontWeightBold:   typography.fontWeightBold,
      h1: { fontSize: typography.scale['5xl'],  fontWeight: typography.fontWeightBold,     lineHeight: 1.2  },
      h2: { fontSize: typography.scale['4xl'],  fontWeight: typography.fontWeightBold,     lineHeight: 1.25 },
      h3: { fontSize: typography.scale['3xl'],  fontWeight: typography.fontWeightSemiBold, lineHeight: 1.3  },
      h4: { fontSize: typography.scale['2xl'],  fontWeight: typography.fontWeightSemiBold, lineHeight: 1.35 },
      h5: { fontSize: typography.scale.xl,      fontWeight: typography.fontWeightMedium,   lineHeight: 1.4  },
      h6: { fontSize: typography.scale.lg,      fontWeight: typography.fontWeightSemiBold, lineHeight: 1.4  },
      subtitle1: { fontSize: typography.scale.md, fontWeight: typography.fontWeightMedium },
      subtitle2: { fontSize: typography.scale.sm, fontWeight: typography.fontWeightMedium },
      body1:  { fontSize: typography.scale.md, lineHeight: 1.6 },
      body2:  { fontSize: typography.scale.sm, lineHeight: 1.6 },
      caption:  { fontSize: typography.scale.xs },
      overline: { fontSize: '0.6875rem', fontWeight: typography.fontWeightSemiBold, letterSpacing: '0.08em', textTransform: 'uppercase' },
      button:   { fontSize: typography.scale.sm, fontWeight: typography.fontWeightSemiBold, textTransform: 'none' },
    },

    spacing: spacingUnit,
    shape: { borderRadius: shape.borderRadius },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': { boxSizing: 'border-box' },
          html: {
            fontSize: '90%',
          },
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? colors.dark.elevated : colors.grey[300]} transparent`,
            '&::-webkit-scrollbar':       { width: 6 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 3, backgroundColor: isDark ? colors.dark.elevated : colors.grey[300] },
          },
        },
      },

      // ── Button ──────────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSm,
            fontWeight: typography.fontWeightSemiBold,
            paddingTop: 7,
            paddingBottom: 7,
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
            color: '#ffffff',
            '& .MuiButton-startIcon': { color: '#ffffff' },
            '&:hover': {
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              color: '#ffffff',
            },
            '&.Mui-disabled': {
              color: 'rgba(255,255,255,0.55)',
              '& .MuiButton-startIcon': { color: 'rgba(255,255,255,0.55)' },
            },
          },
          outlinedPrimary: {
            borderColor: `${colors.primary[500]}66`,
            '&:hover': { borderColor: colors.primary[500], backgroundColor: `${colors.primary[500]}11` },
          },
        },
      },

      // ── Card ────────────────────────────────────────────────────────────────
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: surface.surface,
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd,
          },
        },
      },

      // ── Paper ───────────────────────────────────────────────────────────────
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: surface.surface,
            border: `1px solid ${surface.border}`,
          },
        },
      },

      // ── AppBar ──────────────────────────────────────────────────────────────
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: surface.sidebar,
            borderBottom: `1px solid ${surface.border}`,
            boxShadow: 'none',
          },
        },
      },

      // ── Drawer ──────────────────────────────────────────────────────────────
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: surface.sidebar,
            borderRight: `1px solid ${surface.border}`,
            boxShadow: 'none',
          },
        },
      },

      // ── ListItemButton (nav items) ──────────────────────────────────────────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSm,
            marginBottom: 2,
            paddingTop: 7,
            paddingBottom: 7,
            '&.Mui-selected': {
              backgroundColor: `${colors.primary[500]}1a`,
              color: colors.primary[isDark ? 400 : 600],
              '& .MuiListItemIcon-root': { color: colors.primary[isDark ? 400 : 600] },
              '&:hover': { backgroundColor: `${colors.primary[500]}26` },
            },
            '&:hover': { backgroundColor: surface.hover },
          },
        },
      },

      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 34, color: isDark ? 'rgba(255,255,255,0.45)' : colors.grey[500] } },
      },

      // ── Chip ────────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: shape.borderRadiusSm,
            ...(ownerState.variant === 'outlined' && {
              borderColor: isDark ? 'rgba(255,255,255,0.12)' : colors.grey[200],
              ...(ownerState.color === 'success' && {
                color: isDark ? colors.success.light : colors.success.dark,
                borderColor: isDark ? 'rgba(34,197,94,0.28)' : 'rgba(34,197,94,0.24)',
                bgcolor: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.08)',
                '& .MuiChip-icon': { color: isDark ? colors.success.light : colors.success.dark },
              }),
              ...((ownerState.color === 'default' || ownerState.color === undefined) && {
                color: isDark ? 'rgba(255,255,255,0.72)' : colors.grey[700],
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : colors.grey[50],
                '& .MuiChip-icon': { color: isDark ? 'rgba(255,255,255,0.55)' : colors.grey[500] },
              }),
            }),
          }),
        },
      },

      // ── TextField ───────────────────────────────────────────────────────────
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: shape.borderRadiusSm,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : colors.grey[50],
              '& fieldset': { borderColor: surface.border },
              '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.2)' : colors.grey[400] },
            },
          },
        },
      },

      // ── Select ──────────────────────────────────────────────────────────────
      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          outlined: {
            borderRadius: shape.borderRadiusSm,
          },
        },
      },

      // ── Table ───────────────────────────────────────────────────────────────
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: surface.border, padding: '10px 16px' },
          head: {
            fontWeight: typography.fontWeightSemiBold,
            color: isDark ? 'rgba(255,255,255,0.45)' : colors.grey[500],
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            backgroundColor: isDark ? colors.dark.elevated : colors.grey[50],
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: surface.hover },
            '&:last-child td': { borderBottom: 'none' },
          },
        },
      },

      // ── Tooltip ─────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? colors.dark.elevated : colors.grey[800],
            border: `1px solid ${surface.border}`,
            fontSize: typography.scale.xs,
            borderRadius: shape.borderRadiusSm,
          },
        },
      },

      // ── Divider ─────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: { root: { borderColor: surface.border } },
      },

      // ── Avatar ──────────────────────────────────────────────────────────────
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: `${colors.primary[500]}33`,
            color: colors.primary[400],
            fontWeight: typography.fontWeightSemiBold,
          },
        },
      },

      // ── LinearProgress ──────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: shape.borderRadiusFull, height: 6 },
        },
      },

      // ── Menu ────────────────────────────────────────────────────────────────
      MuiMenu: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          paper: {
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd,
            backgroundColor: isDark ? colors.dark.elevated : '#ffffff',
          },
        },
      },

      // ── Dialog ──────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: isDark ? colors.dark.elevated : '#ffffff',
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd,
          },
        },
      },

      // ── Tabs ────────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: typography.fontWeightMedium,
            fontSize: typography.scale.sm,
            minHeight: 40,
            '&.Mui-selected': { fontWeight: typography.fontWeightSemiBold },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 2 },
          root: { minHeight: 40 },
        },
      },
    },
  };
}

export const darkTheme  = createTheme(buildThemeOptions('dark'));
export const lightTheme = createTheme(buildThemeOptions('light'));

/** Default export = dark theme (matches mockup default) */
export const theme = darkTheme;

export type PaddiqTheme = typeof theme;
export type GridlineTheme = PaddiqTheme;
